import {skipToken} from '@reduxjs/toolkit/query';
import {type BaseFlagProps, Flag} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import type {BusinessCaseResponseBody} from '@dms/api/sales';
import {useGetSaleVehicleFromSaleVehicleIdQuery} from '@dms/api/vehicle';
import {getBusinessCaseStateFlag, getBusinessCaseTypeFlag, useGetFlags} from '@dms/shared';

import type {CustomerInsightsBusinessCaseRecord} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsEnrichmentStatus} from './CustomerInsightsEnrichmentStatus';
import {CustomerInsightsLeadQualityFlag} from './CustomerInsightsLeadQualityFlag';

interface CustomerInsightsBusinessCaseFlagsProps {
  record: CustomerInsightsBusinessCaseRecord;
  businessCase?: BusinessCaseResponseBody;
}

export function CustomerInsightsBusinessCaseFlags(props: CustomerInsightsBusinessCaseFlagsProps) {
  const saleVehicleId = props.businessCase?.saleVehicleId;
  const saleVehicleQuery = useGetSaleVehicleFromSaleVehicleIdQuery(
    isNil(saleVehicleId) ? skipToken : {vehicleId: saleVehicleId}
  );
  const {currentData: saleVehicle} = saleVehicleQuery;
  const [detailFlags] = useGetFlags(props.businessCase, saleVehicle);
  const fallbackFlags = [
    getBusinessCaseTypeFlag(props.record.type),
    getBusinessCaseStateFlag(props.record.state),
  ].filter((flag): flag is BaseFlagProps => !isNil(flag));
  const flags = isNil(props.businessCase) ? fallbackFlags : detailFlags;
  const leadQuality = match(props.businessCase?.leadQuality ?? props.record.leadQuality?.value)
    .with(Pattern.string, (value) => ({value}))
    .otherwise(() => undefined);

  return (
    <CustomerInsightsEnrichmentStatus
      isLoading={saleVehicleQuery.isLoading || saleVehicleQuery.isFetching}
      isError={saleVehicleQuery.isError}
      retry={saleVehicleQuery.refetch}
    >
      {flags.map((flag) => (
        <Flag {...flag} key={`${flag.colorScheme}-${flag.label}`} size="small" />
      ))}
      <CustomerInsightsLeadQualityFlag leadQuality={leadQuality} />
    </CustomerInsightsEnrichmentStatus>
  );
}
